"use client";

import { useId } from "react";

import { resolveKitPattern, resolveTeamColors, type LogoPattern } from "@/lib/colors";

type Props = {
  name: string;
  colors?: string[];
  pattern?: string;
  number?: string;
  size?: number;
  className?: string;
};

const SHIRT =
  "M38 8 L26 12 L8 24 L16 44 L26 39 L26 96 L94 96 L94 39 L104 44 L112 24 L94 12 L82 8 C78 18 70 22 60 22 C50 22 42 18 38 8 Z";

/** Узор футболки (координаты 120×100). */
function shirtPattern(pattern: LogoPattern, second: string) {
  switch (pattern) {
    case "stripes":
      return (
        <>
          {[30, 50, 70, 90].map((x) => (
            <rect key={x} x={x - 5} y="0" width="10" height="100" fill={second} />
          ))}
        </>
      );
    case "pinstripes":
      return (
        <>
          {[30, 38, 46, 54, 62, 70, 78, 86].map((x) => (
            <rect key={x} x={x} y="0" width="2" height="100" fill={second} />
          ))}
        </>
      );
    case "hoops":
      return (
        <>
          {[30, 50, 70, 90].map((y) => (
            <rect key={y} x="0" y={y - 5} width="120" height="10" fill={second} />
          ))}
        </>
      );
    case "sash":
      return <path d="M14 96 L96 6 L112 6 L30 100 Z" fill={second} />;
    case "halves":
      return <rect x="60" y="0" width="60" height="100" fill={second} />;
    case "chevron":
      return <path d="M0 34 L60 66 L120 34 L120 50 L60 82 L0 50 Z" fill={second} />;
    case "quarters":
      return (
        <>
          <rect x="60" y="0" width="60" height="52" fill={second} />
          <rect x="0" y="52" width="60" height="48" fill={second} />
        </>
      );
    case "diagonal":
      return (
        <g transform="rotate(-40 60 50)" fill={second}>
          {[-40, -24, -8, 8, 24, 40, 56, 72, 88, 104, 120, 136].map((x) => (
            <rect key={x} x={x} y="-60" width="8" height="220" />
          ))}
        </g>
      );
    case "checker":
      return (
        <g fill={second}>
          {Array.from({ length: 8 }, (_, col) =>
            Array.from({ length: 7 }, (_, row) =>
              (col + row) % 2 === 0 ? (
                <rect key={`${col}-${row}`} x={col * 15} y={row * 15} width="15" height="15" />
              ) : null,
            ),
          )}
        </g>
      );
    case "wave":
      return (
        <path
          d="M0 30 Q15 18 30 30 T60 30 T90 30 T120 30 L120 48 Q105 60 90 48 T60 48 T30 48 T0 48 Z"
          fill={second}
        />
      );
    case "frame":
      return <rect x="8" y="8" width="104" height="80" fill="none" stroke={second} strokeWidth="9" />;
    default:
      return null;
  }
}

/** Превью формы: футболка с узором и шорты третьего цвета. */
export default function TeamKit({ name, colors, pattern, number = "10", size = 120, className = "" }: Props) {
  const uid = useId().replace(/:/g, "");
  const clipId = `shirt-${uid}`;

  const palette = resolveTeamColors(name, colors);
  const base = palette[0];
  const second = palette[1] ?? palette[0];
  const trim = palette[2] ?? palette[1] ?? palette[0];
  const kit = resolveKitPattern(name, pattern);
  const numberColor = kit === "solid" ? second : "#ffffff";

  return (
    <svg
      width={size}
      height={(size * 150) / 120}
      viewBox="0 0 120 150"
      role="img"
      aria-label={`Форма клуба ${name}`}
      className={className}
    >
      <defs>
        <clipPath id={clipId}>
          <path d={SHIRT} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        <rect x="0" y="0" width="120" height="100" fill={base} />
        {shirtPattern(kit, second)}
      </g>

      {/* манжеты и ворот */}
      <path d="M8 24 L16 44 L26 39 L19 20 Z" fill={trim} opacity="0.9" />
      <path d="M112 24 L104 44 L94 39 L101 20 Z" fill={trim} opacity="0.9" />
      <path d="M38 8 C42 18 50 22 60 22 C70 22 78 18 82 8 L76 7 C72 14 67 17 60 17 C53 17 48 14 44 7 Z" fill={trim} />
      <path d={SHIRT} fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1.5" />

      <text
        x="60"
        y="68"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="26"
        fontWeight="900"
        fill={numberColor}
        stroke="rgba(0,0,0,0.35)"
        strokeWidth="1"
      >
        {number}
      </text>

      {/* шорты */}
      <path d="M28 100 L92 100 L96 138 L66 138 L60 120 L54 138 L24 138 Z" fill={trim} />
      <path d="M28 100 L92 100 L92.6 106 L27.4 106 Z" fill={base} opacity="0.85" />
      <path
        d="M28 100 L92 100 L96 138 L66 138 L60 120 L54 138 L24 138 Z"
        fill="none"
        stroke="rgba(0,0,0,0.35)"
        strokeWidth="1.5"
      />
    </svg>
  );
}
